import type { HTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The one loading spinner. Announces itself: `role="status"` + `aria-busy`
 * with an sr-only label, so assistive tech hears that the surface is
 * loading rather than silence while the DOM changes under it.
 *
 * ONE live region per loading surface. Render this once for the surface
 * that is loading; do not also add `role="status"` to skeleton rows or
 * nest a second indicator inside it — two live regions for one load
 * announce twice.
 */
export interface LoadingIndicatorProps extends HTMLAttributes<HTMLDivElement> {
  /** What is loading. Read by screen readers; not painted. Default "Loading…". */
  label?: string;
  /** Spinner size. Default "md". */
  size?: 'sm' | 'md' | 'lg';
}

const SIZE = { sm: 'h-4 w-4', md: 'h-6 w-6', lg: 'h-8 w-8' } as const;

export function LoadingIndicator({ label = 'Loading…', size = 'md', className, ...rest }: LoadingIndicatorProps) {
  return (
    // PR #375: {...rest} previously came AFTER role/aria-busy/aria-live, so a
    // caller passing role="presentation" (or any aria override) silently
    // stripped the live-region semantics. Spreading first means the explicit
    // a11y attributes always win.
    <div
      {...rest}
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={cn('flex items-center justify-center', className)}
    >
      <Loader2 aria-hidden="true" className={cn('animate-spin text-muted-foreground', SIZE[size])} />
      <span className="sr-only">{label}</span>
    </div>
  );
}
